import React, { useEffect, useState, useMemo } from 'react';
import { X, Bell, BellOff, Trash2, Settings, Filter, ChevronDown } from 'lucide-react';
import { useAlertStore, Alert, AlertType } from '../../store/alertStore';
import { AlertSettingsModal } from '../AlertSettingsModal';
import './AlertsPanel.css';

interface AlertsPanelProps {
  onClose: () => void;
}

type FilterType = 'all' | 'price' | 'volume';
type StatusFilter = 'all' | 'active' | 'paused';

const AlertsPanel: React.FC<AlertsPanelProps> = ({ onClose }) => {
  const {
    alerts,
    isLoading,
    fetchAllAlerts,
    deleteAlert,
    pauseAlert,
    resumeAlert,
  } = useAlertStore();

  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [symbolFilter, setSymbolFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch all alerts on mount
  useEffect(() => {
    fetchAllAlerts();
  }, [fetchAllAlerts]);

  // Get unique symbols for filter
  const symbols = useMemo(() => {
    const symbolSet = new Set(alerts.map((a) => a.symbol));
    return Array.from(symbolSet).sort();
  }, [alerts]);

  // Apply filters
  const filteredAlerts = useMemo(() => {
    return alerts.filter((alert) => {
      // Type filter
      if (filterType === 'price') {
        if (alert.alertType !== 'price_above' && alert.alertType !== 'price_below') {
          return false;
        }
      } else if (filterType === 'volume') {
        if (alert.alertType !== 'volume_spike' && alert.alertType !== 'volume_above') {
          return false;
        }
      }

      // Status filter
      if (statusFilter === 'active' && alert.paused) {
        return false;
      }
      if (statusFilter === 'paused' && !alert.paused) {
        return false;
      }

      // Symbol filter
      if (symbolFilter !== 'all' && alert.symbol !== symbolFilter) {
        return false;
      }

      return true;
    });
  }, [alerts, filterType, statusFilter, symbolFilter]);

  // Group alerts by symbol
  const groupedAlerts = useMemo(() => {
    const groups: Record<string, Alert[]> = {};
    filteredAlerts.forEach((alert) => {
      if (!groups[alert.symbol]) {
        groups[alert.symbol] = [];
      }
      groups[alert.symbol].push(alert);
    });
    return groups;
  }, [filteredAlerts]);

  const handleTogglePause = async (alert: Alert) => {
    if (alert.paused) {
      await resumeAlert(alert.id);
    } else {
      await pauseAlert(alert.id);
    }
  };

  const handleDelete = async (alertId: string) => {
    if (confirm('Delete this alert?')) {
      await deleteAlert(alertId);
    }
  };

  const handlePauseAll = async () => {
    const activeAlerts = filteredAlerts.filter((a) => !a.paused);
    for (const alert of activeAlerts) {
      await pauseAlert(alert.id);
    }
  };

  const handleResumeAll = async () => {
    const pausedAlerts = filteredAlerts.filter((a) => a.paused);
    for (const alert of pausedAlerts) {
      await resumeAlert(alert.id);
    }
  };

  const handleDeleteAll = async () => {
    if (confirm(`Delete all ${filteredAlerts.length} filtered alerts?`)) {
      for (const alert of filteredAlerts) {
        await deleteAlert(alert.id);
      }
    }
  };

  const getAlertTypeLabel = (type: AlertType) => {
    switch (type) {
      case 'price_above':
        return '↑ Price Above';
      case 'price_below':
        return '↓ Price Below';
      case 'volume_spike':
        return '📊 Vol Spike';
      case 'volume_above':
        return '📊 Vol Above';
    }
  };

  const getAlertTypeClass = (type: AlertType) => {
    if (type === 'volume_spike' || type === 'volume_above') return 'volume';
    return 'price';
  };

  const formatTriggerMode = (mode: string) => {
    switch (mode) {
      case 'once_per_minute':
        return '1/min';
      case 'once_per_bar':
        return '1/bar';
      case 'on_cross':
        return 'Cross';
      default:
        return mode;
    }
  };

  return (
    <div className="alerts-panel-backdrop" onClick={onClose}>
      <div className="alerts-panel" onClick={(e) => e.stopPropagation()}>
        <div className="alerts-panel-header">
          <h2>
            <Bell size={20} />
            Manage Alerts
          </h2>
          <div className="alerts-panel-header-actions">
            <button
              className={`alerts-filter-toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              <span>Filters</span>
              <ChevronDown size={14} className={showFilters ? 'rotated' : ''} />
            </button>
            <button className="alerts-panel-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="alerts-panel-filters">
            <div className="filter-group">
              <label>Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as FilterType)}
              >
                <option value="all">All Types</option>
                <option value="price">Price Alerts</option>
                <option value="volume">Volume Alerts</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="paused">Paused Only</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Symbol</label>
              <select
                value={symbolFilter}
                onChange={(e) => setSymbolFilter(e.target.value)}
              >
                <option value="all">All Symbols</option>
                {symbols.map((symbol) => (
                  <option key={symbol} value={symbol}>
                    {symbol}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {filteredAlerts.length > 0 && (
          <div className="alerts-panel-bulk-actions">
            <span className="bulk-count">
              {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
            </span>
            <button className="bulk-btn pause" onClick={handlePauseAll}>
              <BellOff size={14} />
              Pause All
            </button>
            <button className="bulk-btn resume" onClick={handleResumeAll}>
              <Bell size={14} />
              Resume All
            </button>
            <button className="bulk-btn delete" onClick={handleDeleteAll}>
              <Trash2 size={14} />
              Delete All
            </button>
          </div>
        )}

        {/* Alerts List */}
        <div className="alerts-panel-content">
          {isLoading ? (
            <div className="alerts-loading">Loading alerts...</div>
          ) : filteredAlerts.length === 0 ? (
            <div className="alerts-empty">
              <Bell size={48} />
              <p>No alerts found</p>
              <span>Click the bell icon in the action bar to create alerts</span>
            </div>
          ) : (
            Object.entries(groupedAlerts).map(([symbol, symbolAlerts]) => (
              <div key={symbol} className="alerts-symbol-group">
                <div className="alerts-symbol-header">
                  <span className="alerts-symbol-name">{symbol}</span>
                  <span className="alerts-symbol-count">
                    {symbolAlerts.length} alert{symbolAlerts.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="alerts-list">
                  {symbolAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`alert-item ${alert.paused ? 'paused' : ''}`}
                    >
                      <div className="alert-item-main">
                        <span
                          className={`alert-type-badge ${getAlertTypeClass(alert.alertType)}`}
                        >
                          {getAlertTypeLabel(alert.alertType)}
                        </span>
                        <span className="alert-level">
                          {alert.alertType === 'price_above' || alert.alertType === 'price_below'
                            ? alert.price?.toFixed(2)
                            : alert.alertType === 'volume_spike'
                            ? `${alert.volumeMultiplier?.toFixed(1)}x avg`
                            : `${Math.round(alert.volumeThreshold || 0).toLocaleString()} vol`}
                        </span>
                        <span className="alert-trigger-mode">
                          {formatTriggerMode(alert.triggerMode)}
                        </span>
                        <span
                          className={`alert-status ${alert.paused ? 'paused' : 'active'}`}
                        >
                          {alert.paused ? 'Paused' : 'Active'}
                        </span>
                      </div>
                      <div className="alert-item-actions">
                        <button
                          className="alert-item-btn pause"
                          onClick={() => handleTogglePause(alert)}
                          title={alert.paused ? 'Resume' : 'Pause'}
                        >
                          {alert.paused ? <Bell size={14} /> : <BellOff size={14} />}
                        </button>
                        <button
                          className="alert-item-btn settings"
                          onClick={() => setSelectedAlert(alert)}
                          title="Settings"
                        >
                          <Settings size={14} />
                        </button>
                        <button
                          className="alert-item-btn delete"
                          onClick={() => handleDelete(alert.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      {alert.lastTriggeredAt && (
                        <div className="alert-item-last-triggered">
                          Last triggered:{' '}
                          {new Date(alert.lastTriggeredAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {selectedAlert && (
        <AlertSettingsModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </div>
  );
};

export default AlertsPanel;
